/* istanbul ignore file */

import { Routes } from '@angular/router';
import { UserAPIModelsERoles } from 'user-ts-ng-client';

import { authenticatedGuard } from '@core/guards/authenticated.guard';
import { contractAdvisorSubscriptionGuard } from '@core/guards/contract-advisor-subscription.guard';
import { externalUserOnlyGuard } from '@core/guards/external-user-only.guard';
import { globalAdminGuard } from '@core/guards/global-admin.guard';
import { internalUserLandingGuard } from '@core/guards/internal-user-landing.guard';
import { organizationAccessGuard } from '@core/guards/organization-access.guard';
import { PrismAssessmentGuard } from '@core/guards/prism-assessment.guard';
import { roleGuard } from '@core/guards/role.guard';
import { invoiceValidationDirtyGuard } from '@features/invoices/guards/invoice-validation-dirty.guard';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('@features/home/components/home/home.component').then((c) => c.HomeComponent),
    title: 'SRM Momentum',
    canActivate: [authenticatedGuard, internalUserLandingGuard],
  },
  {
    path: 'advisor',
    canActivate: [authenticatedGuard, externalUserOnlyGuard, contractAdvisorSubscriptionGuard],
    data: { breadcrumbLabel: 'Advisor' },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@features/advisor/components/contract-advisor/contract-advisor.component').then(
            (m) => m.ContractAdvisorComponent,
          ),
        data: { title: 'Contract Advisor' },
      },
      {
        path: 'contracts/:id',
        loadComponent: () =>
          import('@features/advisor/components/contract-details/contract-details.component').then(
            (m) => m.ContractDetailsComponent,
          ),
        data: { title: 'Contract Details' },
      },
      {
        path: 'contract-review',
        loadComponent: () =>
          import('@features/advisor/components/market-opportunity/market-opportunity.component').then(
            (m) => m.MarketOpportunityComponent,
          ),
        data: { title: 'Contracts Needing Review' },
      },
      {
        path: 'market-detail',
        loadComponent: () =>
          import('@features/advisor/components/market-detail/market-detail.component').then(
            (m) => m.MarketDetailComponent,
          ),
        data: { title: 'Card Processing Market Assessment' },
      },
      {
        path: 'invoice-validation',
        loadComponent: () =>
          import('@features/invoices/components/invoice-validation/invoice-validation.component').then(
            (m) => m.InvoiceValidationComponent,
          ),
        data: { title: 'Invoice Validation' },
      },
    ],
  },
  {
    path: 'dashboard',
    redirectTo: '',
    pathMatch: 'full',
    canActivate: [authenticatedGuard],
  },

  {
    path: 'contracts',
    loadComponent: () =>
      import('@features/contracts/components/contracts/contracts.component').then(
        (m) => m.ContractsComponent,
      ),
    data: { title: 'Contracts' },
    canActivate: [authenticatedGuard],
  },
  {
    path: 'clients/:id',
    data: {
      title: 'Client Detail',
      allowedRoles: [
        UserAPIModelsERoles.GlobalAdmin,
        UserAPIModelsERoles.SrmContributor,
        UserAPIModelsERoles.SrmManager,
      ],
    },
    canActivate: [authenticatedGuard, roleGuard, organizationAccessGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('@features/clients/components/client-detail/client-detail.component').then(
            (m) => m.ClientDetailComponent,
          ),
      },
      {
        path: 'users',
        pathMatch: 'full',
        redirectTo: '',
      },
      {
        path: 'users/add',
        data: { title: 'Add Client User', allowedRoles: [UserAPIModelsERoles.GlobalAdmin] },
        canActivate: [roleGuard],
        loadComponent: () =>
          import('@features/organization/components/user/create/user-create.component').then(
            (m) => m.UserCreateComponent,
          ),
      },
      {
        path: 'users/edit/:userEmail',
        data: { title: 'Edit Client User', allowedRoles: [UserAPIModelsERoles.GlobalAdmin] },
        canActivate: [roleGuard],
        loadComponent: () =>
          import('@features/organization/components/user/edit/user-edit.component').then(
            (m) => m.UserEditComponent,
          ),
      },
    ],
  },
  {
    path: 'organization',
    loadChildren: () =>
      import('@features/organization/organization.routes').then((r) => r.ORGANIZATION_ROUTES),
    data: {
      title: 'Organizations',
      breadcrumbUrl: '/organizations',
      breadcrumbAllowedRoles: [UserAPIModelsERoles.GlobalAdmin],
      allowedRoles: [
        UserAPIModelsERoles.GlobalAdmin,
        UserAPIModelsERoles.SrmContributor,
        UserAPIModelsERoles.SrmManager,
        UserAPIModelsERoles.OrganizationAdmin,
      ],
    },
    canActivate: [authenticatedGuard, roleGuard],
  },
  {
    path: 'organizations',
    loadChildren: () =>
      import('@features/organizations/organizations.routes').then((r) => r.ORGANIZATIONS_ROUTES),
    data: { title: 'Organizations', allowedRoles: [UserAPIModelsERoles.GlobalAdmin] },
    canActivate: [authenticatedGuard, globalAdminGuard],
  },
  {
    path: 'project/:id',
    loadComponent: () =>
      import('@features/projects/components/project-detail/project-detail.component').then(
        (m) => m.ProjectDetailComponent,
      ),
    data: {
      title: 'Engagement',
      allowedRoles: [
        UserAPIModelsERoles.GlobalAdmin,
        UserAPIModelsERoles.SrmContributor,
        UserAPIModelsERoles.SrmManager,
        UserAPIModelsERoles.OrganizationAdmin,
        UserAPIModelsERoles.OrganizationContributor,
      ],
    },
    canActivate: [authenticatedGuard, roleGuard],
  },
  {
    path: 'projects',
    loadComponent: () =>
      import('@features/projects/components/projects-list/projects-list.component').then(
        (m) => m.ProjectsListComponent,
      ),
    data: {
      title: 'Engagements',
      allowedRoles: [
        UserAPIModelsERoles.GlobalAdmin,
        UserAPIModelsERoles.SrmContributor,
        UserAPIModelsERoles.SrmManager,
      ],
    },
    canActivate: [authenticatedGuard, roleGuard],
  },
  {
    path: 'invoices',
    loadComponent: () =>
      import('@features/invoices/components/invoices/invoices.component').then(
        (m) => m.InvoicesComponent,
      ),
    data: {
      title: 'Invoices',
      allowedRoles: [
        UserAPIModelsERoles.GlobalAdmin,
        UserAPIModelsERoles.SrmContributor,
        UserAPIModelsERoles.SrmManager,
      ],
    },
    canActivate: [authenticatedGuard, roleGuard],
  },
  {
    path: 'billing-details/:id',
    loadComponent: () =>
      import('@features/billing-details/components/billing-detail-page/billing-detail-page.component').then(
        (m) => m.BillingDetailPageComponent,
      ),
    data: {
      title: 'Billing Detail',
      breadcrumbLabel: 'Billing Detail',
      breadcrumbUrl: '/invoices',
      allowedRoles: [
        UserAPIModelsERoles.GlobalAdmin,
        UserAPIModelsERoles.SrmContributor,
        UserAPIModelsERoles.SrmManager,
      ],
    },
    canActivate: [authenticatedGuard, roleGuard],
  },
  {
    path: 'invoice',
    data: {
      breadcrumbLabel: 'Invoices',
      breadcrumbUrl: '/invoices',
      allowedRoles: [
        UserAPIModelsERoles.GlobalAdmin,
        UserAPIModelsERoles.SrmContributor,
        UserAPIModelsERoles.SrmManager,
      ],
    },
    canActivate: [authenticatedGuard, roleGuard],
    children: [
      {
        path: 'validation/:guid',
        loadComponent: () =>
          import('@features/invoices/components/invoice-validation-workspace/invoice-validation-workspace.component').then(
            (m) => m.InvoiceValidationWorkspaceComponent,
          ),
        data: {
          title: 'Invoice Validation',
          breadcrumbLabel: 'Validation',
          allowedRoles: [
            UserAPIModelsERoles.GlobalAdmin,
            UserAPIModelsERoles.SrmContributor,
            UserAPIModelsERoles.SrmManager,
          ],
        },
        canDeactivate: [invoiceValidationDirtyGuard],
      },
    ],
  },
  {
    path: 'trackers',
    loadComponent: () =>
      import('@features/trackers/components/trackers/trackers.component').then(
        (m) => m.TrackersComponent,
      ),
    data: {
      title: 'Trackers',
      allowedRoles: [
        UserAPIModelsERoles.GlobalAdmin,
        UserAPIModelsERoles.SrmContributor,
        UserAPIModelsERoles.SrmManager,
      ],
    },
    canActivate: [authenticatedGuard, roleGuard],
  },
  {
    path: 'tracker/:id',
    loadComponent: () =>
      import('@features/trackers/components/tracker/tracker.component').then(
        (m) => m.TrackerComponent,
      ),
    data: {
      title: 'Tracker',
      allowedRoles: [
        UserAPIModelsERoles.GlobalAdmin,
        UserAPIModelsERoles.SrmContributor,
        UserAPIModelsERoles.SrmManager,
      ],
    },
    canActivate: [authenticatedGuard, roleGuard],
  },
  {
    path: 'vendors',
    loadComponent: () =>
      import('@features/vendors/components/vendors/vendors.component').then(
        (m) => m.VendorsComponent,
      ),
    data: { title: 'Vendors' },
    canActivate: [authenticatedGuard],
    children: [
      {
        path: 'map',
        loadComponent: () =>
          import('@features/vendors/components/vendor-map/vendor-map.component').then(
            (m) => m.VendorMapComponent,
          ),
        data: { title: 'VendorMap', subtitle: 'View annual spend by vendor and contract' },
        canActivate: [authenticatedGuard],
      },
      {
        path: 'vendor-map',
        redirectTo: 'map',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'services',
    canActivate: [authenticatedGuard],
    children: [
      {
        path: 'map',
        loadComponent: () =>
          import('@features/services/components/service-map/service-map.component').then(
            (m) => m.ServiceMapComponent,
          ),
        data: {
          title: 'ServiceMap',
          subtitle: 'View and explore annual spend by service category and subcategory.',
        },
        canActivate: [authenticatedGuard],
      },
    ],
  },
  {
    path: 'vendor/:id',
    loadComponent: () =>
      import('@features/vendor/components/vendor-detail/vendor-detail.component').then(
        (m) => m.VendorDetailComponent,
      ),
    data: { title: 'Vendor Details' },
    canActivate: [authenticatedGuard],
  },

  {
    path: 'assessment',
    loadComponent: () =>
      import('./pages/prism-assessment/prism-assessment.component').then(
        (c) => c.PrismAssessmentComponent,
      ),
    canActivate: [authenticatedGuard, contractAdvisorSubscriptionGuard, PrismAssessmentGuard],
    data: { layout: 'fullscreen' },
    children: [
      {
        path: ':facetId',
        loadComponent: () =>
          import('@features/vendor/components/prism-assessment/prism-form/prism-form.component').then(
            (c) => c.PrismFormComponent,
          ),
      },
    ],
  },
  {
    path: 'health',
    loadComponent: () =>
      import('@features/health/components/health-summary/health-summary.component').then(
        (m) => m.HealthSummaryComponent,
      ),
    data: { title: 'Health Summary' },
    canActivate: [authenticatedGuard],
  },
  {
    path: '**',
    redirectTo: '',
  },
];
